import React from 'react';
import { useInView } from 'react-intersection-observer';
import 'animate.css';

const Features: React.FC = () => {
  const [firstScreenshotRef, firstScreenshotInView] = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });

  const [secondScreenshotRef, secondScreenshotInView] = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });

  return (
    <>
            {/* For the first time, everyone in one place */}
      <section className="py-20 px-6 bg-white" aria-labelledby="unified-contacts-heading">
        <div className="max-w-6xl mx-auto">
          <h2 id="unified-contacts-heading" className="font-serif text-5xl font-light leading-tight tracking-tight text-gray-900 mb-8 text-center">
            For the first time, everyone in one place
          </h2>
          <p className="font-sans text-xl font-normal text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed text-center">
            We pull in contacts and communications from everywhere - email, meetings, address book, WhatsApp, LinkedIn, spreadsheets, and more. It's like having a personal detective for your relationships, but without the creepy surveillance van.
          </p>
          
          {/* Screenshot */}
          <div className="mt-12 mb-8" ref={firstScreenshotRef}>
            <div className="w-full max-w-6xl mx-auto">
              <div className="p-4 bg-gradient-to-r from-purple-500 via-pink-500 to-red-500 rounded-2xl shadow-2xl">
                <img 
                  src="/screens/db.png" 
                  alt="Relatable personal CRM dashboard showing unified contact database with integrated communications from email, meetings, WhatsApp, LinkedIn, and other sources" 
                  className={`w-full h-auto rounded-xl transition-all duration-1000 ${
                    firstScreenshotInView ? 'animate__animated animate__zoomIn' : 'opacity-0 scale-95'
                  }`}
                  loading="lazy"
                />
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12">
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <p className="font-sans text-sm font-medium text-gray-700">Email</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 0h6m-6 0l-6 6m6-6l6 6" />
                </svg>
              </div>
              <p className="font-sans text-sm font-medium text-gray-700">Meetings</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <p className="font-sans text-sm font-medium text-gray-700">Address Book</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <p className="font-sans text-sm font-medium text-gray-700">WhatsApp</p>
            </div>
          </div>
        </div>
      </section>

      {/* Remember who matters */}
      <section className="py-20 px-6 bg-gray-50" aria-labelledby="remember-who-matters-heading">
        <div className="max-w-6xl mx-auto">
          <h2 id="remember-who-matters-heading" className="font-serif text-5xl font-light leading-tight tracking-tight text-gray-900 mb-8 text-center">
            Remember who matters
          </h2>
          <p className="font-sans text-xl font-normal text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed text-center">
            Our spheres system helps you easily organize and prioritize your relationships for you and your team. Think of it as relationship triage - we help you focus on the people who actually matter, not just the ones who are loudest.
          </p>
          
          {/* Screenshot */}
          <div className="mt-12 mb-8">
            <div className="w-full max-w-6xl mx-auto">
              <div className="p-4 bg-gradient-to-r from-green-500 via-emerald-500 to-teal-500 rounded-2xl shadow-2xl">
                <img 
                  src="/screens/spheres.png" 
                  alt="Relatable spheres system interface showing contact organization and relationship prioritization with color-coded categories" 
                  className="w-full h-auto rounded-xl"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
          
          <div className="bg-white p-8 rounded-lg max-w-2xl mx-auto border border-gray-200">
            <div className="flex items-center justify-center space-x-4 mb-6">
              <div className="w-4 h-4 rounded-full bg-purple-600"></div>
              <div className="w-4 h-4 rounded-full bg-blue-600"></div>
              <div className="w-4 h-4 rounded-full bg-green-600"></div>
              <div className="w-4 h-4 rounded-full bg-yellow-600"></div>
            </div>
            <p className="font-sans text-base font-normal text-gray-600 leading-relaxed text-center">
              Organize contacts into meaningful spheres - from your inner circle to professional network. 
              Relatable helps you focus on the relationships that matter most. No more treating your mom like just another contact in your database.
            </p>
          </div>
        </div>
      </section>

      {/* Remember who to talk to */}
      <section className="py-20 px-6 bg-white" aria-labelledby="remember-who-to-talk-to-heading">
        <div className="max-w-6xl mx-auto">
          <h2 id="remember-who-to-talk-to-heading" className="font-serif text-5xl font-light leading-tight tracking-tight text-gray-900 mb-8 text-center">
            Remember who to talk to
          </h2>
          <p className="font-sans text-xl font-normal text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed text-center">
            We'll surface relationships that you could stay in touch with - whether it's been too long, 
            it's their birthday or anniversary, changed jobs, or other important moments. We're basically your relationship's nagging conscience, but in a good way.
          </p>
          
          {/* Screenshot */}
          <div className="mt-12 mb-8" ref={secondScreenshotRef}>
            <div className="w-full max-w-6xl mx-auto">
              <div className="p-4 bg-gradient-to-r from-blue-500 via-cyan-500 to-teal-500 rounded-2xl shadow-2xl">
                <img 
                  src="/screens/dashboard.png" 
                  alt="Relatable CRM dashboard interface showing relationship reminders, follow-up suggestions, and contact prioritization system" 
                  className={`w-full h-auto rounded-xl transition-all duration-1000 ${
                    secondScreenshotInView ? 'animate__animated animate__zoomIn' : 'opacity-0 scale-95'
                  }`}
                  loading="lazy"
                />
              </div>
            </div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 mt-12">
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 0h6m-6 0l-6 6m6-6l6 6" />
                </svg>
              </div>
              <h3 className="font-sans text-lg font-semibold text-gray-900 mb-2">Too Long</h3>
              <p className="font-sans text-sm text-gray-600">Relationships that need reconnecting</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <h3 className="font-sans text-lg font-semibold text-gray-900 mb-2">Birthdays & Anniversaries</h3>
              <p className="font-sans text-sm text-gray-600">Never miss important celebrations</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.608-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H8a2 2 0 01-2-2V8a2 2 0 012-2V6" />
                </svg>
              </div>
              <h3 className="font-sans text-lg font-semibold text-gray-900 mb-2">Job Changes</h3>
              <p className="font-sans text-sm text-gray-600">Stay updated on career moves</p>
            </div>
          </div>
        </div>
      </section>

      {/* Easily reach out */}
      <section className="py-20 px-6 bg-gray-50" aria-labelledby="easily-reach-out-heading">
        <div className="max-w-6xl mx-auto">
          <h2 id="easily-reach-out-heading" className="font-serif text-5xl font-light leading-tight tracking-tight text-gray-900 mb-8 text-center">
            Easily reach out
          </h2>
          <p className="font-sans text-xl font-normal text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed text-center">
            We have split messaging and individual messaging capabilities. Split messaging is magic. 
            And yes, it goes out through your normal email accounts. No more copy-pasting the same message to 50 people and hoping no one notices.
          </p>
          
          {/* Screenshot */}
          <div className="mt-12 mb-8">
            <div className="w-full max-w-6xl mx-auto">
              <div className="p-4 bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 rounded-2xl shadow-2xl">
                <img 
                  src="/screens/splitmessage.png" 
                  alt="Relatable split messaging interface showing personalized email composition with multiple recipients and individual message customization" 
                  className="w-full h-auto rounded-xl"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
          
          <div className="bg-white p-8 rounded-lg max-w-2xl mx-auto border border-gray-200">
            <div className="flex items-center justify-center mb-6">
              <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center mr-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </div>
              <h3 className="font-sans text-xl font-semibold text-gray-900">Split Messaging</h3>
            </div>
            <p className="font-sans text-base font-normal text-gray-600 leading-relaxed text-center">
              Send personalized messages to multiple contacts at once. Each recipient gets their own 
              customized message while you maintain the personal touch. It's like having a personal assistant who actually remembers everyone's name and doesn't just call them "sir" or "ma'am."
            </p>
          </div>
        </div>
      </section>

      {/* Work with your team */}
      <section className="py-20 px-6 bg-white" aria-labelledby="work-with-team-heading">
        <div className="max-w-6xl mx-auto">
          <h2 id="work-with-team-heading" className="font-serif text-5xl font-light leading-tight tracking-tight text-gray-900 mb-8 text-center">
            Work with your team
          </h2>
          <p className="font-sans text-xl font-normal text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed text-center">
            Whether you're a team of one or a team of ten, Relatable remembers who matters. 
            Who was the last person to talk to our client? Who is supposed to? No more playing relationship detective in Slack threads.
          </p>
          
          {/* Screenshot */}
          <div className="mt-12 mb-8">
            <div className="w-full max-w-6xl mx-auto">
              <div className="p-4 bg-gradient-to-r from-indigo-500 via-purple-500 to-violet-500 rounded-2xl shadow-2xl">
                <img 
                  src="/screens/team.png" 
                  alt="Relatable team collaboration interface showing shared contact management, relationship insights, and team communication history" 
                  className="w-full h-auto rounded-xl"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 mt-12">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="font-sans text-lg font-semibold text-gray-900 mb-3">Team Collaboration</h3>
              <p className="font-sans text-sm text-gray-600">
                Share relationship insights and communication history across your team. 
                Never lose track of who talked to whom. It's like having a shared brain for your relationships.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="font-sans text-lg font-semibold text-gray-900 mb-3">Accountability</h3>
              <p className="font-sans text-sm text-gray-600">
                Know who's responsible for staying in touch with key relationships. 
                Assign and track follow-up responsibilities. No more "I thought you were handling that" conversations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Sign Up CTA */}
      <section className="py-24 px-6 bg-gradient-to-r from-purple-600 via-pink-600 to-red-600" aria-labelledby="signup-cta-heading">
        <div className="max-w-4xl mx-auto text-center">
          <h2 id="signup-cta-heading" className="font-serif text-6xl font-bold leading-tight tracking-tight text-white mb-6">
            Ready to Remember Who Matters?
          </h2>
          <p className="font-sans text-2xl font-normal text-white/90 mb-8 max-w-3xl mx-auto leading-relaxed">
            Stop playing relationship detective. Start building meaningful connections that last.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
            <a 
              href="https://www.relatable.one/signup" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-white text-purple-600 font-sans text-xl font-bold px-12 py-6 rounded-2xl shadow-2xl hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 hover:shadow-3xl cursor-pointer inline-block"
            >
              🚀 Sign Up for Relatable
            </a>
            <a 
              href="https://www.youtube.com/watch?v=NGrLb6W5YOM" 
              target="_blank" 
              rel="noopener noreferrer"
              className="border-2 border-white text-white font-sans text-lg font-semibold px-8 py-6 rounded-2xl hover:bg-white hover:text-purple-600 transition-all duration-300 transform hover:scale-105 cursor-pointer inline-block"
            >
              😓 Nah I really don't care about people
            </a>
          </div>
          
          <div className="flex flex-wrap justify-center items-center gap-8 text-white/80 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-2xl">✨</span>
              <span>Free 10-day trial</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl">🔒</span>
              <span>No credit card required</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl">⚡</span>
              <span>Setup in 5 minutes</span>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Features; 